import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { scrollToTop } from '@/domains/productDiscovery/ProductPage/common/utils/scrollToTop';
import { Attribute } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';

type UseExpandedTableReturn = [boolean, (toExpand: boolean) => Promise<void>];

export type BehaviorType = 'unfold' | 'unfold-fold' | 'action';

export type BehaviorArgType =
  | {
      type: 'unfold-fold';
      to: React.RefObject<HTMLElement>;
    }
  | {
      type: 'action';
      action: () => unknown;
    }
  | {
      type: 'unfold';
    };

export function useExpandedTable(
  attributes: Attribute[],
  title: string | undefined,
  defaultExpanded = false,
  behavior: BehaviorArgType = { type: 'unfold' },
): UseExpandedTableReturn {
  const [isExpanded, setExpanded] = useState(defaultExpanded);
  const behaviorRef = useRef(behavior);

  useEffect(() => {
    behaviorRef.current = behavior;
  }, [behavior]);

  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [attributes, defaultExpanded]);

  const onExpand = useCallback(
    async (toExpand: boolean) => {
      if (
        (behaviorRef.current.type === 'unfold-fold' ||
          behaviorRef.current.type === 'unfold') &&
        toExpand
      ) {
        setExpanded(true);
      }

      if (behaviorRef.current.type === 'unfold-fold' && !toExpand) {
        await scrollToTop(behaviorRef.current.to.current);
        setExpanded(false);
      }

      if (behaviorRef.current.type === 'action') {
        behaviorRef.current.action();
      }
    },
    [behaviorRef],
  );

  return [isExpanded, onExpand];
}
